// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';

// Import authentication components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import { EmailVerification } from './components/auth/EmailVerification';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { ResetPassword } from './components/auth/ResetPassword';

// Import layout and dashboard components
import Layout from './components/layout/Layout';
import AdminDashboard from './components/dashboard/AdminDashboard';
import CoreAdminDashboard from './components/dashboard/CoreAdminDashboard';

// Import feature components
import StaffManagement from './components/staff/StaffManagement';
import AttendanceManagement from './components/attendance/AttendanceManagement';
import Reports from './components/reports/Reports';
import LeaveManagement from './components/leaves/LeaveManagement';
import PremisesManagement from './components/premises/PremisesManagement';
import PaymentGateways from './components/payments/PaymentGateways';
import LicensePricing from './components/payments/LicensePricing';
import ShiftManagement from './components/shifts/ShiftManagement';

// Import new license management components
import LicensePurchase from './components/subscriptions/LicensePurchase';
import Subscriptions from './components/subscriptions/Subscriptions';
import CoreAdminPaymentManagement from './components/admin/CoreAdminPaymentManagement';
import PaymentAnalyticsDashboard from './components/admin/PaymentAnalyticsDashboard';
import OrganizationManagement from './components/admin/OrganizationManagement';
import AdminUserManagement from './components/admin/AdminUserManagement';
import RefundManagement from './components/admin/RefundManagement';
import AuditTrailManagement from './components/admin/AuditTrailManagement';
import MyOrganization from './components/organization/MyOrganization';
import LandingPageManager from './components/admin/LandingPageManager';
import HelpContentManagement from './components/admin/HelpContentManagement';
import InvoiceGenerator from './components/admin/InvoiceGenerator';
import TestimonialSubmission from './components/testimonials/TestimonialSubmission';
import Settings from './components/settings/Settings';

// Import notification components
import NotificationCenter from './components/notifications/NotificationCenter';
import BroadcastNotification from './components/notifications/BroadcastNotification';

// Import profile components
import Profile from './components/profile/Profile';
import ChangePassword from './components/profile/ChangePassword';

// Import payment result components
import PaymentSuccess from './components/payments/PaymentSuccess';
import PaymentCancelled from './components/payments/PaymentCancelled';
import PaymentFailed from './components/payments/PaymentFailed';

// Import styles
import './App.css';
import './styles/global.css';
import './styles/components.css';

import { Logo } from './hooks/useLogo';

// Loading component
const LoadingScreen = () => (
    <div className="loading-screen">
        <div className="loading-content">
            <div className="spinner"></div>
            <p>Loading...</p>
        </div>
        <style jsx>{`
      .loading-screen {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--gradient);
      }
      .loading-content {
        text-align: center;
        color: white;
      }
      .spinner {
        width: 50px;
        height: 50px;
        border: 4px solid rgba(255,255,255,0.3);
        border-top: 4px solid white;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 1rem;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
    </div>
);

// Protected Route Component with enhanced permission checking
const ProtectedRoute = ({ children, requiredRole = null, requiredPermission = null }) => {
    const { user, isAuthenticated, hasPermission } = useAuth();

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Check specific role requirement
    if (requiredRole && user?.user_type !== requiredRole) {
        return <Navigate to="/dashboard" replace />;
    }

    // Check permission requirement
    if (requiredPermission && !hasPermission(requiredPermission)) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// App Router Component
const AppRouter = () => {
    const { isAuthenticated, user, loading } = useAuth();

    // Show loading screen while checking authentication
    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <Routes>
            {/* Public Authentication Routes */}
            <Route
                path="/login"
                element={
                    isAuthenticated ? (
                        <Navigate to="/dashboard" replace />
                    ) : (
                        <Login />
                    )
                }
            />
            <Route
                path="/register"
                element={
                    isAuthenticated ? (
                        <Navigate to="/dashboard" replace />
                    ) : (
                        <Register />
                    )
                }
            />

            {/* Email Verification Routes */}
            <Route
                path="/verify-email"
                element={<EmailVerification />}
            />
            <Route
                path="/forgot-password"
                element={
                    isAuthenticated ? (
                        <Navigate to="/dashboard" replace />
                    ) : (
                        <ForgotPassword />
                    )
                }
            />
            <Route
                path="/reset-password"
                element={
                    isAuthenticated ? (
                        <Navigate to="/dashboard" replace />
                    ) : (
                        <ResetPassword />
                    )
                }
            />

            {/* Payment Result Routes - Accessible after payment gateway redirect */}
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancelled" element={<PaymentCancelled />} />
            <Route path="/payment-failed" element={<PaymentFailed />} />

            {/* Protected Routes */}
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route
                    index
                    element={<Navigate to="/dashboard" replace />}
                />

                {/* Dashboard Routes */}
                <Route
                    path="dashboard"
                    element={
                        user?.user_type === 'core_admin' ? (
                            <CoreAdminDashboard />
                        ) : (
                            <AdminDashboard />
                        )
                    }
                />

                {/* Core Admin Routes */}
                <Route
                    path="organizations"
                    element={
                        <ProtectedRoute requiredRole="core_admin">
                            <OrganizationManagement />
                        </ProtectedRoute>
                    }
                />

                {/* Client Admin - My Organization */}
                <Route
                    path="my-organization"
                    element={
                        <ProtectedRoute requiredRole="client_admin">
                            <MyOrganization />
                        </ProtectedRoute>
                    }
                />

                {/* Core Admin - Payment Management */}
                <Route
                    path="admin/payment-management"
                    element={
                        <ProtectedRoute requiredRole="core_admin">
                            <CoreAdminPaymentManagement />
                        </ProtectedRoute>
                    }
                />

                {/* Core Admin - Landing Page Management */}
                <Route
                    path="admin/landing-page"
                    element={
                        <ProtectedRoute requiredRole="core_admin">
                            <LandingPageManager />
                        </ProtectedRoute>
                    }
                />

                {/* Core Admin - Help & Support Content (Mobile App) */}
                <Route
                    path="admin/help-content"
                    element={
                        <ProtectedRoute requiredRole="core_admin">
                            <HelpContentManagement />
                        </ProtectedRoute>
                    }
                />

                {/* Core Admin - Refund Management */}
                <Route
                    path="admin/refunds"
                    element={
                        <ProtectedRoute requiredRole="core_admin">
                            <RefundManagement />
                        </ProtectedRoute>
                    }
                />

                {/* Invoice Generator - Core Admin and Client Admin */}
                <Route
                    path="admin/invoices"
                    element={
                        <ProtectedRoute>
                            <InvoiceGenerator />
                        </ProtectedRoute>
                    }
                />

                {/* System Settings - Core Admin, Client Admin, App Admin */}
                <Route
                    path="admin/settings"
                    element={
                        <ProtectedRoute>
                            <Settings />
                        </ProtectedRoute>
                    }
                />

                {/* Admin User Management - For client_admin and core_admin */}
                <Route
                    path="admin/users"
                    element={
                        <ProtectedRoute>
                            <AdminUserManagement />
                        </ProtectedRoute>
                    }
                />

                {/* Staff Management */}
                <Route
                    path="staff"
                    element={
                        <ProtectedRoute requiredPermission="staff.view">
                            <StaffManagement />
                        </ProtectedRoute>
                    }
                />

                {/* User Management (Core Admin view) */}
                <Route
                    path="users"
                    element={
                        <ProtectedRoute requiredRole="core_admin">
                            <StaffManagement />
                        </ProtectedRoute>
                    }
                />

                {/* Attendance Management */}
                <Route
                    path="attendance"
                    element={
                        <ProtectedRoute requiredPermission="attendance.view">
                            <AttendanceManagement />
                        </ProtectedRoute>
                    }
                />

                {/* Reports */}
                <Route
                    path="reports"
                    element={
                        <ProtectedRoute requiredPermission="reports.view">
                            <Reports />
                        </ProtectedRoute>
                    }
                />

                {/* Leave Management */}
                <Route
                    path="leaves"
                    element={
                        <ProtectedRoute requiredPermission="leaves.view">
                            <LeaveManagement />
                        </ProtectedRoute>
                    }
                />

                {/* Premises Management */}
                <Route
                    path="premises"
                    element={
                        <ProtectedRoute requiredPermission="premises.manage">
                            <PremisesManagement />
                        </ProtectedRoute>
                    }
                />

                {/* Payment Routes - Core Admin Only */}
                <Route
                    path="payment-gateways"
                    element={
                        <ProtectedRoute requiredRole="core_admin">
                            <PaymentGateways />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="license-pricing"
                    element={
                        <ProtectedRoute requiredRole="core_admin">
                            <LicensePricing />
                        </ProtectedRoute>
                    }
                />

                {/* Subscription Routes */}
                <Route
                    path="subscriptions"
                    element={
                        <ProtectedRoute requiredPermission="settings.view">
                            <Subscriptions />
                        </ProtectedRoute>
                    }
                />

                {/* License Purchase Route - Client Admin */}
                <Route
                    path="subscriptions/purchase"
                    element={
                        <ProtectedRoute requiredPermission="settings.view">
                            <LicensePurchase />
                        </ProtectedRoute>
                    }
                />

                {/* Subscription Management - View current subscription */}
                <Route
                    path="subscriptions/manage"
                    element={
                        <ProtectedRoute requiredPermission="settings.view">
                            <Subscriptions />
                        </ProtectedRoute>
                    }
                />

                {/* Shift Management */}
                <Route
                    path="shifts"
                    element={
                        <ProtectedRoute requiredPermission="staff.view">
                            <ShiftManagement />
                        </ProtectedRoute>
                    }
                />

                {/* Testimonials (Client Admin) */}
                <Route
                    path="testimonials"
                    element={
                        <ProtectedRoute requiredRole="client_admin">
                            <TestimonialSubmission />
                        </ProtectedRoute>
                    }
                />

                {/* Payment Analytics (Core Admin + Organization Admin) */}
                <Route
                    path="analytics"
                    element={
                        <ProtectedRoute requiredPermission="reports.view">
                            <PaymentAnalyticsDashboard />
                        </ProtectedRoute>
                    }
                />

                {/* Core Admin - Audit Trail */}
                <Route
                    path="admin/audit-trail"
                    element={
                        <ProtectedRoute requiredRole="core_admin">
                            <AuditTrailManagement />
                        </ProtectedRoute>
                    }
                />

                {/* Profile Routes */}
                <Route
                    path="profile"
                    element={<Profile />}
                />

                <Route
                    path="change-password"
                    element={<ChangePassword />}
                />

                {/* Notification Routes */}
                <Route
                    path="notifications"
                    element={<NotificationCenter />}
                />

                <Route
                    path="notifications/broadcast"
                    element={<BroadcastNotification />}
                />
            </Route>

            {/* Fallback Route */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

// Main App Component
function App() {
    useEffect(() => {
        const applyDynamicFavicon = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/public/favicon`);
                const result = await response.json();
                const faviconPath = result?.data?.favicon || result?.favicon;

                if (!faviconPath) return;

                const faviconUrl = faviconPath.startsWith('http')
                    ? faviconPath
                    : `${process.env.REACT_APP_API_URL}${faviconPath}`;

                let link = document.querySelector("link[rel='icon']");
                if (!link) {
                    link = document.createElement('link');
                    link.setAttribute('rel', 'icon');
                    document.head.appendChild(link);
                }

                // Cache-bust so browser does not keep old favicon aggressively.
                link.setAttribute('href', `${faviconUrl}${faviconUrl.includes('?') ? '&' : '?'}v=${Date.now()}`);
            } catch (error) {
                // Keep default favicon when API fetch fails.
            }
        };

        applyDynamicFavicon();
    }, []);

    return (
        <AuthProvider>
            <Router>
                <div className="App">
                    <AppRouter />
                </div>
            </Router>
        </AuthProvider>
    );
}

export default App;
